import { useEffect, useState } from 'react'

import useIsServer from 'hooks/useIsServer'

const loadVideo = (video: HTMLVideoElement, videoSrc: string) => {
  // eslint-disable-next-line global-require
  const Hls = require('hls.js')
  let hls: typeof Hls

  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // Some browers (safari and ie edge) support HLS natively
    // eslint-disable-next-line no-param-reassign
    video.src = videoSrc
  } else if (Hls?.isSupported()) {
    // This will run in all other modern browsers
    hls = new Hls({
      enableWorker: true,
      lowLatencyMode: true,
      capLevelToPlayerSize: true,
      backBufferLength: 90,
    })
    hls.loadSource(videoSrc)
    hls.attachMedia(video)
  } else {
    // eslint-disable-next-line no-console
    console.error("This is a legacy browser that doesn't support MSE")
  }

  return hls
}

export const useVideoPlaying = (
  videoRef: React.MutableRefObject<HTMLVideoElement>
) => {
  const [videoPlaying, setVideoPlaying] = useState(false)

  useEffect(() => {
    videoRef.current?.addEventListener('play', () => setVideoPlaying(true))
    videoRef.current?.addEventListener('pause', () => setVideoPlaying(false))

    return () => {
      videoRef.current?.removeEventListener('play', () => setVideoPlaying(true))
      videoRef.current?.removeEventListener('pause', () =>
        setVideoPlaying(false)
      )
    }
  }, [videoRef])

  return videoPlaying
}

export const useVideoEnded = (
  videoRef: React.MutableRefObject<HTMLVideoElement>,
  playing: boolean
) => {
  const [ended, setEnded] = useState(false)

  const handleEnded = () => setEnded(true)

  useEffect(() => {
    videoRef.current?.addEventListener('ended', handleEnded)

    return () => {
      videoRef.current?.removeEventListener('ended', handleEnded)
    }
  }, [videoRef])

  useEffect(() => {
    if (playing) {
      setEnded(false)
    }
  }, [playing])

  return ended
}

export const useForcePause = (
  videoRef: React.MutableRefObject<HTMLVideoElement>,
  forcePause: boolean
) => {
  useEffect(() => {
    if (videoRef.current != null && forcePause === true) {
      videoRef.current.pause()
    }
  }, [forcePause, videoRef])
}

export const useLoadVideo = (
  videoRef: React.MutableRefObject<HTMLVideoElement>,
  src: string
) => {
  const isServer = useIsServer()
  useEffect(() => {
    let hls: any // need some way to type Hls instance here
    if (videoRef.current && src) {
      hls = loadVideo(videoRef.current, src)
    }

    return () => {
      if (hls) {
        hls.destroy()
      }
    }
  }, [videoRef, src, isServer])
}

export const useReloadOnEnded = (
  videoRef: React.MutableRefObject<HTMLVideoElement>,
  src: string,
  videoEnded: boolean
) => {
  useEffect(() => {
    if (videoEnded && src && videoRef.current) {
      loadVideo(videoRef.current, src)
    }
  }, [videoRef, videoEnded])
}
